import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from '@rsos/components/capstone/base';
import { CRGSpatialLayerSelector } from '@rsos/components/capstone/common/Map/SpatialLayers/selectors';
import { STATIC_LAYER_MIN_ZOOM } from '@rsos/components/capstone/common/Map/SpatialLayers/spatialLayersSources';
import { useZoomAndLocationContext } from '@rsos/components/capstone/common/Map/ZoomAndLocationContext';
import {
  showPopover,
  hidePopover,
} from '@rsos/components/capstone/common/Map/useDeckGL/MapPopover/mapPopoverSlice';
import StaticLayerGL from '@rsos/components/capstone/common/Map/useDeckGL/StaticLayersGL/StaticLayerGL';
import { psapSelector } from '@rsos/reduxDataStore/src/features/capstone/psapInfo/psapInfoSlice';
import isSpatialLayersSummaryEnabled from '@rsos/utils/isSpatialLayersSummaryEnabled';
import useDebounce from '@rsos/utils/useDebounce';
import {
  fetchCRGDeeplink,
  resolveSpatialObject,
  setActiveCRGObject,
  setActiveCRGObjectFloor,
} from '../../../../actions/spatialLayers';
import ExpandedMapDialog from '../../MapDialog/ExpandedMapDialog';

/**
 * CRG map handling using Deck.GL
 * @param {Object} overlay -
 * @param {removedID} removedID - Phone number of a call that was removed from the queue
 */
const CRGGL = ({ overlay, removedID, isMapIconDisabled }) => {
  const [activeMarkerId, setActiveMarkerId] = useState(null);

  const dispatch = useDispatch();
  const { zoomLevel } = useZoomAndLocationContext();

  const psaps = useSelector(psapSelector);
  const { currentOrg } = useSelector(state => state.sinatra.user);

  // Check capability for optimized endpoints (per-agency)
  const hasOptimizedEndpoints = useMemo(
    () => isSpatialLayersSummaryEnabled(psaps),
    [psaps],
  );

  const CRGLayers = useSelector(state => {
    return CRGSpatialLayerSelector(state.spatialLayers.staticLayers) || [];
  });

  const activeCrgObject = useSelector(
    state => state.spatialLayers.activeCrgObject,
  );

  const objectDetailsCache = useSelector(
    state => state.spatialLayers.objectDetailsCache,
  );
  const cacheRef = useRef(objectDetailsCache);

  useEffect(() => {
    cacheRef.current = objectDetailsCache;
  }, [objectDetailsCache]);

  const handleMarkerClick = useCallback(
    spatialObject => {
      const objectId = spatialObject.id;

      // Set active object immediately with summary to open modal
      dispatch(setActiveCRGObject(spatialObject));
      setActiveMarkerId(objectId);

      // Fetch details and deeplink concurrently
      dispatch(resolveSpatialObject(spatialObject, hasOptimizedEndpoints));
      dispatch(fetchCRGDeeplink(currentOrg.id, objectId));
    },
    [dispatch, hasOptimizedEndpoints, currentOrg.id],
  );

  const handleCloseCrgCard = useCallback(() => {
    dispatch(setActiveCRGObject(null));
    dispatch(setActiveCRGObjectFloor(null));
    dispatch(hidePopover());
  }, [dispatch]);

  const isModalOpen = Object.keys(activeCrgObject.spatialObject).length > 0;
  useEffect(() => {
    if (!isModalOpen) {
      setActiveMarkerId(null);
    }
  }, [isModalOpen]);

  const handleMarkerHover = useDebounce(
    o => {
      if (o) {
        const objectId = o.spatialObject?.id;
        const cachedDetails = cacheRef.current?.[objectId];
        const displayObject = cachedDetails || o.spatialObject;

        const pop = {
          spatialLayerKey: displayObject?.external_id,
          spatialLayer: o.spatialLayer,
          spatialObject: displayObject,
        };
        dispatch(showPopover(pop));
      } else {
        dispatch(hidePopover());
      }
    },
    100,
    [],
  );
  const layerVisible = zoomLevel >= STATIC_LAYER_MIN_ZOOM;

  return (
    <>
      {CRGLayers.map((spatialLayer, i) => {
        return (
          <StaticLayerGL
            key={`${spatialLayer.id}-${i}`}
            overlay={overlay}
            spatialLayer={spatialLayer}
            onClick={handleMarkerClick}
            activeMarkerId={activeMarkerId}
            filled={false}
            onHover={handleMarkerHover}
            isMapIconDisabled={isMapIconDisabled}
            layerVisible={layerVisible}
          />
        );
      })}
      {isModalOpen && (
        <Modal
          id="expanded-map-dialog"
          onClose={handleCloseCrgCard}
          isOpened
          transparent
          disableBackdropClick
        >
          <ExpandedMapDialog
            onClose={handleCloseCrgCard}
            removedID={removedID}
          />
        </Modal>
      )}
    </>
  );
};

export default CRGGL;
